import type { ResourceKinds } from 'v5/core/entity-values/values.types';
import { schemaErrorFallback } from 'v4/entities/common/schemaErrorFallback';
import { maybe, rd, RemoteData } from '@passionware/monads';
import type { CustomFieldDefinition, Group, Resource } from '__types__';
import { get } from 'lodash';
import { type RefObject, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getGroupList } from 'store/v1/groups/groups.selectors.js';
import { selectAppId } from 'store/v1/auth/auth.selectors';
import { getRates } from 'store/v1/rates/rates.actions.js';
import CURRENCIES from 'v1/helpers/DropdownDataSets/CURRENCIES';
import { withinSettings } from 'v4/core/appRoutes';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import type {
  EntitySchema,
  RateCardOption,
  ResourceMembership
} from 'v5/core/entity-schema/entitySchema.types';
import type { BrowsingScope } from 'v5/core/form-schema/formSchema.types';
import { collectOverrides } from 'v5/core/entity-schema/resource/collectOverrides';
import { membershipFromRows } from 'v5/core/entity-schema/resource/resourceMembership';
import {
  resolveResourceSchema,
  type DelegatedCoreField
} from 'v5/core/entity-schema/resource/resolveResourceSchema';
import { resourceCodecs } from 'v5/core/entity-values/codecs/registry';
import type { CodecRegistry } from 'v5/core/entity-values/values.types';
import type { ResourceWriteModel } from 'v5/core/resource-write/ResourceWriteModel';
import { getResourceGroupOptions } from 'v5/features/resources/resourceGroupOptions';
import { useResourceFormKit } from 'v5/patterns/forms/schema-form/fields/resourceFormKit';
import type {
  FormKit,
  SchemaFormHandle,
  UnitBindings
} from 'v5/patterns/forms/schema-form/schemaForm.types';
import { WithServices } from 'v5/platform/ts/services';
import { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import { Permission } from 'v5/services/front/PermissionService/PermissionService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithGlobalSettingsService } from 'v5/services/internal/GlobalSettingsService/GlobalSettingsService';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithResourceTypeBindingsService } from 'v5/services/internal/ResourceTypeBindingsService/ResourceTypeBindingsService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithGeolocationService } from 'v5/services/io/GeolocationService/GeolocationService';
import { WithSystemSettingsService } from 'v5/services/internal/SystemSettingsService/SystemSettingsService';
import { WithResourceMutationService } from 'v5/services/io/ResourceMutationService/ResourceMutationService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { WithUserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import { WithUploadService } from 'v5/services/io/UploadService/UploadService';
import { buildResourceBindings } from './resourceBindings';
import {
  amountToBuilder,
  builderWriteModel,
  currencyDefinitionIds,
  resourceToBuilder
} from './resourceBuilderUnits';
import { useResourceFormKitConfig } from './resourceFormKitConfig';
import { useResourceSchemaCopy } from './useResourceSchemaCopy';

export type ResourceFormServices = WithServices<
  [
    WithAppScopeService,
    WithAccessService,
    WithResourceService,
    WithResourceMutationService,
    WithResourceTypeService,
    WithResourceTypeBindingsService,
    WithMetastructureMappingService,
    WithEnterpriseService,
    WithGlobalSettingsService,
    WithTagService,
    WithGeolocationService,
    /** The browsing workspace's country, for new phone numbers. */
    WithSystemSettingsService,
    /** Profile picture upload (create) + its error toast. */
    WithUploadService,
    WithToastService,
    /** Opening a referenced resource (agent, organisation). */
    WithUserFlowService
  ]
>;

export type CreateDraft = {
  resourceTypeId: number;
  profilePicture: string | null;
  membership: ResourceMembership;
};

export type ResourceFormModelInput =
  | {
      mode: 'edit';
      resourceId: number;
      forceReadOnly: boolean;
      /** Core fields the host chrome edits itself (see `ResolveResourceSchemaInput`). */
      presentedByHost: ReadonlyArray<DelegatedCoreField>;
    }
  | {
      mode: 'create';
      draft: CreateDraft;
      /** The create form, so an agent picked there fills the resource's details. */
      form: RefObject<SchemaFormHandle<ResourceKinds> | null>;
      /** `false`: the Global section alone, no workspace sections (see `ResourceCreateForm`). */
      workspaceSections: boolean;
    };

export type ResourceFormModel =
  | {
      mode: 'edit';
      schema: EntitySchema;
      bindings: UnitBindings<ResourceKinds>;
      kit: FormKit<ResourceKinds>;
      codecs: CodecRegistry;
      resource: Resource;
      workspaceIcons: ReadonlyMap<number, { name: string; iconUrl: string }>;
    }
  | {
      mode: 'create';
      schema: EntitySchema;
      kit: FormKit<ResourceKinds>;
      codecs: CodecRegistry;
      writeModel: ResourceWriteModel;
      resourceTypeId: number;
      workspaceIcons: ReadonlyMap<number, { name: string; iconUrl: string }>;
    };

type RateCardRow = {
  id: number;
  name?: string;
  description?: string;
  currency?: string;
  amount?: number;
  interval?: string;
  agency_fee?: number;
  payroll_fee?: number;
  processing_fee?: number;
  archived?: boolean;
};

/** Rate cards from the v1 store (`state.rates`), fetched once on demand; `GET /rates` needs `RATE_READ`. */
function useRateCards(canReadRates: boolean): RateCardOption[] {
  const dispatch = useDispatch();
  const fetched = useSelector((state: unknown) =>
    Boolean(get(state, 'rates.fetched'))
  );
  const loading = useSelector((state: unknown) =>
    Boolean(get(state, 'rates.loading'))
  );
  // A failed fetch must not retry on every render that follows it.
  const failed = useSelector((state: unknown) =>
    Boolean(get(state, 'rates.error'))
  );
  const data = useSelector(
    (state: unknown) =>
      get(state, 'rates.data') as Record<string, RateCardRow> | undefined
  );
  useEffect(() => {
    if (canReadRates && !fetched && !loading && !failed) {
      dispatch(getRates());
    }
  }, [dispatch, canReadRates, fetched, loading, failed]);
  return useMemo(
    () =>
      Object.values(data ?? {})
        .filter(rate => rate.archived !== true)
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        .map(rate => ({
          id: rate.id,
          name: rate.name ?? `Rate card ${rate.id}`,
          description: rate.description ?? '',
          currency: rate.currency ?? '',
          amount: amountToBuilder(rate.amount) ?? null,
          interval: rate.interval ?? 'DAILY',
          agencyFee: rate.agency_fee ?? null,
          payrollFee: rate.payroll_fee ?? null,
          processingFee: rate.processing_fee ?? null
        })),
    [data]
  );
}

const NO_GROUPS: Group[] = [];

/** ISO code as the value, the name as the label: the rate field shows the code itself. */
const currencyOptions = CURRENCIES.map(currency => ({
  value: currency.code,
  label: currency.name
}));

/**
 * Composes services into everything the engine needs: the resolved schema,
 * one binding per unit (edit) and the field kit. Pure logic lives in
 * `core/`; this hook only gathers inputs and memoises outputs.
 */
export function useResourceFormModel(
  input: ResourceFormModelInput,
  services: ResourceFormServices['services']
): RemoteData<ResourceFormModel> {
  const copy = useResourceSchemaCopy();
  const scopeRd = services.appScopeService.useResolvedAppScope();
  const organizationId = rd.tryMap(scopeRd, scope => scope.organizationId);
  const identity = rd.tryMap(scopeRd, appScopeUtils.identityFromResolved);
  const browsing: RemoteData<BrowsingScope> = rd.map(scopeRd, scope =>
    scope.level === 'organization'
      ? { level: 'organization' }
      : { level: 'workspace', workspaceId: scope.workspaceId }
  );

  // A resource that fails validation is edited from its unvalidated data; the
  // host decides whether to show the form for it at all.
  const resourceRd = schemaErrorFallback.recoverSchemaError(
    services.resourceService.useItem(
      input.mode === 'edit' ? input.resourceId : undefined
    )
  );
  const resourceTypeId: number | null =
    input.mode === 'create'
      ? input.draft.resourceTypeId
      : rd.mapOrElse(
          resourceRd,
          resource => resource.resource_type_id ?? null,
          null
        );

  const resourceTypeRd = services.resourceTypeService.useResourceType(
    resourceTypeId ?? undefined
  );
  const metastructureRd = services.metastructureMappingService.useMetastructure(
    maybe.map(
      maybe.of(
        resourceTypeId !== null && organizationId ? organizationId : null
      ),
      organizationId => ({
        type: 'resource' as const,
        subtypeId: resourceTypeId as number,
        scope: { level: 'organization' as const, organizationId }
      })
    )
  );
  const definitionsRd =
    services.metastructureMappingService.useCustomFieldDefinitions();
  const bindingsRd =
    services.resourceTypeBindingsService.useBindings(organizationId);
  const workspacesRd = services.enterpriseService.useWorkspaces(
    organizationId,
    'active'
  );
  const tagsRd = services.tagsService.useTags({ type: 'resource', search: '' });
  const pronouns = services.globalSettingsService.usePronounsOptions();
  const groupList = useSelector(getGroupList) as Group[];
  const tagsFixedRd = services.systemSettingsService.useSetting(
    identity,
    'tags_fixed'
  );
  const can = services.accessService.useCan(identity);
  const canConfigureTagsRd = can({ action: Permission.CONFIGURE_TAGS });
  const canUpdateRd = can({ action: Permission.RESOURCE_UPDATE });
  const canCreateRd = can({ action: Permission.RESOURCE_CREATE });
  const canReadRatesRd = can({ action: Permission.RATE_READ });
  const canUpdateRatesRd = can({ action: Permission.RATE_UPDATE });
  const canConfigureRateCardsRd = can({
    action: Permission.CONFIGURE_RATE_CARDS
  });
  const appId = useSelector(selectAppId);
  const defaultCurrency = useSelector((state: unknown) =>
    String(get(state, 'auth.settings.settings.defaults_currency.value', 'GBP'))
  );
  const rateCards = useRateCards(rd.tryGet(canReadRatesRd) === true);

  // Stable identity: a fresh `[]` per render would rebuild the kit.
  const resource = rd.mapOrElse(resourceRd, r => r, null);
  const selectedGroups = useMemo(
    () => resource?.groups ?? NO_GROUPS,
    [resource]
  );
  const kit = useResourceFormKit(
    useResourceFormKitConfig(
      services,
      groupList,
      selectedGroups,
      resource,
      input.mode === 'create' ? input.form : undefined
    )
  );

  // Memoised combine: a new RemoteData per render would defeat the memo below.
  const combined = rd.useCombine({
    browsing,
    resourceType: resourceTypeRd,
    metastructure: metastructureRd,
    definitions: definitionsRd,
    bindings: bindingsRd,
    workspaces: workspacesRd,
    tags: tagsRd,
    tagsFixed: tagsFixedRd,
    canConfigureTags: canConfigureTagsRd,
    canUpdate: canUpdateRd,
    canCreate: canCreateRd,
    canReadRates: canReadRatesRd,
    canUpdateRates: canUpdateRatesRd,
    canConfigureRateCards: canConfigureRateCardsRd,
    resource: input.mode === 'edit' ? resourceRd : rd.of(null)
  });

  return useMemo(
    () =>
      rd.map(combined, data => {
        const definitions: Record<number, CustomFieldDefinition> =
          data.definitions ?? {};
        const currencyIds = currencyDefinitionIds(definitions);
        const writeModel = builderWriteModel(
          services.resourceMutationService,
          currencyIds
        );
        const workspaceIcons = new Map(
          data.workspaces.map(ws => [
            ws.id,
            { name: ws.name, iconUrl: ws.iconUrl }
          ])
        );
        const workspaceNames = new Map(
          data.workspaces.map(ws => [ws.id, ws.name])
        );
        const resourceTypeModel = data.resourceType.model ?? 'INDIVIDUAL';
        const typeId = data.resourceType.id;
        const options = {
          resourceTypeModel,
          resourceTypeId: typeId,
          tags: data.tags.map(tag => ({
            value: String(tag.id),
            label: tag.tag
          })),
          allowCreateTags: !data.tagsFixed.value && data.canConfigureTags,
          groups: getResourceGroupOptions(groupList, typeId),
          pronouns: pronouns.map(option => ({
            id: option.id,
            value: option.value
          })),
          currencies: currencyOptions,
          defaultCurrency,
          rateCards,
          rateCardsSettingsPath: data.canConfigureRateCards
            ? withinSettings(appId).settings.team.rateCards()
            : null,
          copy
        };

        if (input.mode === 'create') {
          const schema = resolveResourceSchema({
            mode: 'create',
            resourceTypeId: typeId,
            resourceTypeModel,
            globalMetastructure: data.metastructure,
            customFieldDefinitions: definitions,
            bindings: data.bindings,
            workspaces: input.workspaceSections ? data.workspaces : [],
            membership: input.draft.membership,
            browsing: data.browsing,
            overrides: new Map(),
            // The create dialog has no chrome of its own: complete form.
            presentedByHost: [],
            access: {
              canUpdateResource: data.canCreate,
              forceReadOnly: false,
              canReadRates: data.canReadRates,
              canUpdateRates: data.canUpdateRates
            },
            options
          });
          return {
            mode: 'create',
            schema,
            kit,
            codecs: resourceCodecs,
            writeModel,
            resourceTypeId: typeId,
            workspaceIcons
          };
        }

        const resource = resourceToBuilder(
          data.resource as Resource,
          currencyIds
        );
        const overrides = collectOverrides(resource);
        const schema = resolveResourceSchema({
          mode: 'edit',
          resourceTypeId: typeId,
          resourceTypeModel,
          globalMetastructure: data.metastructure,
          customFieldDefinitions: definitions,
          bindings: data.bindings,
          workspaces: data.workspaces,
          membership: membershipFromRows(resource.enterprise_workspaces),
          browsing: data.browsing,
          overrides,
          presentedByHost: input.presentedByHost,
          access: {
            canUpdateResource: data.canUpdate,
            forceReadOnly: input.forceReadOnly,
            canReadRates: data.canReadRates,
            canUpdateRates: data.canUpdateRates
          },
          options
        });
        const bindings = buildResourceBindings({
          schema,
          resource,
          codecs: resourceCodecs,
          writeModel,
          readResource: services.resourceService.ensureItem,
          workspaceNames,
          overrides
        });
        return {
          mode: 'edit',
          schema,
          bindings,
          kit,
          codecs: resourceCodecs,
          resource,
          workspaceIcons
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      combined,
      copy,
      kit,
      groupList,
      pronouns,
      defaultCurrency,
      rateCards,
      appId,
      input
    ]
  );
}
